import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["minMaxFields", "questionGroup", "candidateCheckbox", "minRequiredField", "maxAllowedField", "aggregateBanner", "submitButton"]

  declare minMaxFieldsTarget: HTMLElement
  declare hasMinMaxFieldsTarget: boolean
  declare questionGroupTargets: HTMLElement[]
  declare candidateCheckboxTargets: HTMLInputElement[]
  declare minRequiredFieldTarget: HTMLInputElement
  declare maxAllowedFieldTarget: HTMLInputElement
  declare aggregateBannerTarget: HTMLElement
  declare submitButtonTargets: HTMLButtonElement[]

  connect(): void {
    // Scroll to server-side error block if present (e.g., after min>max submit)
    const errorBlock = this.element.querySelector<HTMLElement>('#choice-group-errors')
    if (errorBlock) {
      errorBlock.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }

    // Initialize visibility based on current radio button state
    this.toggleMinMax()
    this.checkConstraints()
  }

  toggleMinMax(): void {
    if (this.hasMinMaxFieldsTarget) {
      // Get the checked radio button value
      const selectedRadio = this.element.querySelector('input[name="choice_group[display_on_ballot]"]:checked') as HTMLInputElement

      if (selectedRadio) {
        const isDisplayOnBallot = selectedRadio.value === 'true'

        if (isDisplayOnBallot) {
          // Hide min/max when display_on_ballot is true
          this.minMaxFieldsTarget.style.display = 'none'
        } else {
          // Show min/max when display_on_ballot is false
          this.minMaxFieldsTarget.style.display = ''
        }
      }
    }
  }

  checkConstraints(): void {
    const warnings = this.analyzeConstraints()
    this.renderWarnings(warnings)
  }

  private renderWarnings(warnings: { perQuestion: Map<string, { severity: 'blocking' | 'warning', message: string }[]>, aggregate: string[], aggregateSeverity: 'blocking' | 'warning' | null }): void {
    if (warnings.perQuestion.size === 0 && warnings.aggregate.length === 0) {
      this.clearWarnings()
      return
    }

    this.clearWarnings()

    for (const [questionId, messages] of warnings.perQuestion.entries()) {
      const questionEl = this.questionGroupTargets.find(
        el => el.dataset.questionId === questionId
      )
      if (!questionEl) continue

      const warningDiv = questionEl.querySelector<HTMLElement>(
        '.votes--choice-groups--form-component__question-warning'
      )
      if (!warningDiv) continue

      warningDiv.classList.remove('votes--choice-groups--form-component__question-warning--hidden')
      if (messages[0].severity === 'blocking') {
        warningDiv.classList.add('votes--choice-groups--form-component__question-warning--blocking')
      }
      warningDiv.innerHTML = `<i class="fas fa-exclamation-triangle"></i>${messages.map(m => m.message).join('<br>')}`
    }

    if (warnings.aggregate.length > 0) {
      this.aggregateBannerTarget.classList.remove('votes--choice-groups--form-component__aggregate-banner--hidden')
      this.aggregateBannerTarget.classList.remove('votes--choice-groups--form-component__aggregate-banner--warning')
      this.aggregateBannerTarget.classList.remove('votes--choice-groups--form-component__aggregate-banner--blocking')
      const severityClass = warnings.aggregateSeverity === 'blocking'
        ? 'votes--choice-groups--form-component__aggregate-banner--blocking'
        : 'votes--choice-groups--form-component__aggregate-banner--warning'
      this.aggregateBannerTarget.classList.add(severityClass)
      this.aggregateBannerTarget.innerHTML = `
        <i class="fas fa-exclamation-circle"></i><span>${warnings.aggregate.join('<br>')}</span>
      `
    }

    const hasBlocking =
      [...warnings.perQuestion.values()].some(msgs => msgs.some(m => m.severity === 'blocking')) ||
      warnings.aggregateSeverity === 'blocking'
    this.updateSubmitButtons(hasBlocking)
  }

  private clearWarnings(): void {
    for (const questionEl of this.questionGroupTargets) {
      const warningDiv = questionEl.querySelector<HTMLElement>(
        '.votes--choice-groups--form-component__question-warning'
      )
      if (warningDiv) {
        warningDiv.classList.add('votes--choice-groups--form-component__question-warning--hidden')
        warningDiv.innerHTML = ''
      }
    }

    this.aggregateBannerTarget.classList.add('votes--choice-groups--form-component__aggregate-banner--hidden')
    this.aggregateBannerTarget.classList.remove('votes--choice-groups--form-component__aggregate-banner--warning')
    this.aggregateBannerTarget.classList.remove('votes--choice-groups--form-component__aggregate-banner--blocking')
    this.aggregateBannerTarget.innerHTML = ''
    this.updateSubmitButtons(false)
  }

  // Algorithm mirrors app/models/votes/choice_groups/constraint_analyzer.rb#L58-92
  private analyzeConstraints(): { perQuestion: Map<string, { severity: 'blocking' | 'warning', message: string }[]>, aggregate: string[], aggregateSeverity: 'blocking' | 'warning' | null } {
    const warnings = {
      perQuestion: new Map<string, { severity: 'blocking' | 'warning', message: string }[]>(),
      aggregate: [] as string[],
      aggregateSeverity: null as 'blocking' | 'warning' | null
    }

    let totalMaxSelectable: number = 0

    const displayOnBallotRadio = this.element.querySelector('input[name="choice_group[display_on_ballot]"]:checked') as HTMLInputElement
    const isDisplayOnBallot: boolean = displayOnBallotRadio?.value === 'true'
    const minRequired: number = isDisplayOnBallot ? 0 : (parseInt(this.minRequiredFieldTarget.value, 10) || 0)
    const maxAllowed: number = isDisplayOnBallot ? 0 : (parseInt(this.maxAllowedFieldTarget.value, 10) || 1)

    for (const questionElement of this.questionGroupTargets) {
      const questionId: string = questionElement.dataset.questionId ?? ''
      const qMax: number = parseInt(questionElement.dataset.answerMaximum ?? '0', 10)
      const checkedBoxes = questionElement.querySelectorAll(
        '[data-votes--choice-groups--form-component-target="candidateCheckbox"]:checked'
      )
      const slateCount: number = checkedBoxes.length

      const effectiveMax: number = Math.min(slateCount, qMax)
      totalMaxSelectable += effectiveMax

      // Only relevant for "All Choices" mode (display_on_ballot: true) AND single-choice
      // questions (qMax < 2). For multi-choice questions ballot validation handles over-selection.
      if (isDisplayOnBallot && slateCount > qMax && qMax < 2) {
        warnings.perQuestion.set(questionId, [{
          severity: 'blocking',
          message: `${slateCount} choices assigned — question maximum is ${qMax}`
        }])
      }

      // Per-question: ballot minimum unachievable with this slate's max constraint
      // Only relevant when display_on_ballot is false (manual min/max mode)
      if (!isDisplayOnBallot) {
        const answerMinimum: number = parseInt(questionElement.dataset.answerMinimum ?? '0', 10) || 0
        if (answerMinimum > 0) {
          const allCheckboxes = questionElement.querySelectorAll(
            '[data-votes--choice-groups--form-component-target="candidateCheckbox"]'
          )
          const nonSlateCount: number = allCheckboxes.length - slateCount
          const achievable: number = maxAllowed + nonSlateCount

          if (achievable < answerMinimum) {
            const existing = warnings.perQuestion.get(questionId) ?? []
            existing.push({
              severity: 'blocking',
              message: `Ballot minimum ${answerMinimum} — slate maximum ${maxAllowed}`
            })
            warnings.perQuestion.set(questionId, existing)
          }
        }
      }
    }

    if (totalMaxSelectable < minRequired) {
      // Message format mirrors Votes::ChoiceGroups::ConstraintMessages.impossible_minimum
      warnings.aggregate.push(
        `Choices selected ${totalMaxSelectable} — slate minimum is ${minRequired}`
      )
      warnings.aggregateSeverity = 'blocking'
    }

    return warnings
  }

  private updateSubmitButtons(hasBlocking: boolean): void {
    for (const button of this.submitButtonTargets) {
      button.disabled = hasBlocking
      button.classList.toggle('votes--choice-groups--form-component__submit--disabled', hasBlocking)
    }
  }
}
